import React, { FC } from 'react';

import { FileIconVariant } from '../../../assets/icons/file-icon';
import { FileIcon, ThreeDotsIcon } from '../../../assets/icons/index';
import { AlertDialogWrapper } from '../../../components/alert-dialog/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { FormattedDateDistance } from '../../../components/formatted-date-distance/index';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/popover/index';
import { Company, useModalState, VaultHooks } from '../../../hooks/index';
import { StakeholderRole } from '../../../types/stakeholder.types';
import { VaultFile, VaultSelect } from '../../../types/vault.types';
import { formatBytes } from '../../../utils/format-bytes.util';
import { resolveVaultDisplayName } from '../../../utils/get-s3file-original-name.util';
import { isVaultDocusignFile, VaultFileIcon } from '../components/vault-file-icon';
import { VaultFileManageActions } from '../components/vault-file-manage-actions';
import { RenameModal } from '../modals/index';

export type VaultMobileFileProps = {
  handleDelete: (id: string) => void;
  handleDownload: (fileId: string) => void;
  handleRename: (id: string, name: string, link?: string) => void;
  isStakeholdersPath?: boolean;
  item: VaultFile;
};

export const VaultMobileFile: FC<VaultMobileFileProps> = ({
  handleDownload,
  handleDelete,
  handleRename,
  isStakeholdersPath = false,
  item,
}) => {
  const { name, size, updatedAt, id, link } = item;
  const isDocusignFile = isVaultDocusignFile(item);
  const { selectedCompany: { stakeholder } = {} } = Company.useSelected();

  const { viewFile } = VaultHooks.useViewFile();

  const {
    toggler: renameModalToggler,
    isOpen: isOpenRenameModal,
    handleOpenModal: handleOpenRenameModal,
    handleCloseModal: handleCloseRenameModal,
    handleSuccessModal: handleSuccessRenameModal,
    modalData: currentName,
  } = useModalState<string | undefined, unknown, string>({
    onSuccess: (name?: string) => {
      handleRename(id, name || '', link);
    },
  });

  return (
    <>
      <AlertDialogWrapper control={{ onOpenChange: renameModalToggler, open: isOpenRenameModal }}>
        <RenameModal
          currentName={currentName}
          onClose={handleCloseRenameModal}
          onSuccess={handleSuccessRenameModal}
          type={VaultSelect.FILE}
        />
      </AlertDialogWrapper>

      <div className="flex w-full items-center justify-between gap-2 p-4 pr-2">
        <div className="flex w-full items-center gap-3">
          <VaultFileIcon className="size-6" file={item} />
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium text-gray-700">
              {resolveVaultDisplayName(name, link)}
            </span>
            <span className="text-label-sm font-medium text-gray-400">
              <AppFormattedMessage id="LAST_EDIT" />:{' '}
              {updatedAt && <FormattedDateDistance date={new Date(updatedAt)} />} -{' '}
              {formatBytes(size)}
            </span>
          </div>
        </div>
        <Popover>
          <PopoverTrigger className="h-fit w-fit rounded data-[state=open]:shadow-md">
            <div className="flex size-6 rotate-90 items-center justify-center rounded bg-transparent transition-colors hover:bg-gray-100">
              <ThreeDotsIcon />
            </div>
          </PopoverTrigger>
          <PopoverContent className="flex w-fit flex-col gap-[2px] rounded p-2 font-normal shadow-2xl [&>*]:font-inter [&>*]:font-normal">
            <PopoverClose>
              <div
                className="flex w-full items-center gap-2 p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
                onClick={() => viewFile({ fileId: id })}
              >
                <FileIcon className="size-4" />
                <span className="text-sm font-normal text-gray-700">
                  <AppFormattedMessage id="VIEW" />
                </span>
              </div>
              <div
                className="flex w-full items-center gap-2 p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
                onClick={() => handleDownload(id)}
              >
                <FileIcon variant={FileIconVariant.DOWNLOAD} />
                <span className="text-sm font-normal text-gray-700">
                  <AppFormattedMessage id="DOWNLOAD" />
                </span>
              </div>
              {stakeholder?.role !== StakeholderRole.STAKEHOLDER && !isStakeholdersPath && (
                <VaultFileManageActions
                  isDisabled={isDocusignFile}
                  onDelete={() => handleDelete(id)}
                  onRename={() => handleOpenRenameModal(resolveVaultDisplayName(name, link))}
                />
              )}
            </PopoverClose>
          </PopoverContent>
        </Popover>
      </div>
    </>
  );
};
